import React, { useEffect, useState } from 'react';
import validator from 'email-validator';
import ShipmentsActionCreators from 'actions/ShipmentsActionCreators';
import CopyButton from 'components/v2/buttons/CopyButton';
import CargomaticInput from 'components/v2/input';
import { processBulkBol } from 'utils/ShipmentWebAPIUtils';
import { titleize } from 'underscore.string';
import { useTranslation } from 'react-i18next';
import { showToast } from '../../../stores/ToastStore';
import CargomaticModal from '../../v2/modal';
import ProTip from '../../ui/ProTip';
import ShipmentStore from '../../../stores/ShipmentStore';
const columnHeaders = [
    { name: 'SHIPMENT', localizationSlug: 'SHIPMENT' },
    { name: 'SHIPPER', localizationSlug: 'SHIPPER' },
    { name: 'RECIPIENTS', localizationSlug: 'RECIPIENT', count: 2 },
    { name: 'APPLY TO ALL', localizationSlug: 'APPLY_TO_ALL' },
    { name: 'REMOVE', localizationSlug: 'REMOVE' },
];
const BulkBOLGeneration = ({ onHide, shipments }) => {
    const [bolShipments, setBolShipments] = useState([]);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const { t } = useTranslation();
    useEffect(() => {
        ShipmentsActionCreators.getShipmentsById({ shipments });
        ShipmentStore.addChangeListener(handleShipmentsQueryUpdate);
        return () => {
            ShipmentStore.removeChangeListener(handleShipmentsQueryUpdate);
        };
    }, []);
    const handleShipmentsQueryUpdate = async () => {
        try {
            const shipmentsFromAPI = await ShipmentStore.getShipmentsForBulkUpdate();
            setBolShipments(getRefinedShipmentsData(shipmentsFromAPI));
        }
        catch (e) {
            console.error('Error while getting shippments for bulk update: ', e);
        }
    };
    const renderModalComponent = () => {
        return (React.createElement("div", { className: "-m-4 p-4 bg-gray-100 overflow-y-auto", style: { maxHeight: '80vh' } },
            renderProTip(),
            React.createElement("div", { className: "p-4 overflow-x-auto bg-white shadow mt-4" },
                React.createElement("table", { className: "table" },
                    renderColumnHeaders(),
                    renderRows()))));
    };
    const renderRows = () => {
        return (React.createElement("tbody", null, bolShipments.map((shipment) => (React.createElement("tr", { key: shipment._id }, renderColumns(shipment))))));
    };
    const renderColumns = (shipment) => {
        const idKey = `id_${shipment.id}`;
        const shipperNameKey = `shipper_${shipment.id}`;
        const shipmentLink = `/shipments/${shipment.id}`;
        const emailKey = `/emails_/${shipment.id}`;
        const applyAllKey = `apply_${shipment.id}`;
        const removeShipmentKey = `remove_${shipment.id}`;
        const shipperLink = `/users/${shipment.shipper._id}`;
        return [
            React.createElement("td", { className: "px-6 py-4 text-sm border-b border-grey-lighter border-solid text-gray-500", key: idKey },
                React.createElement("a", { href: shipmentLink, target: "_blank", rel: "noreferrer" }, shipment.shipment_id)),
            React.createElement("td", { className: "px-6 py-4 text-sm border-b border-grey-lighter border-solid text-gray-500", key: shipperNameKey, style: { minWidth: '150px', maxWidth: '150px' } },
                React.createElement("a", { href: shipperLink, target: "_blank", rel: "noreferrer" }, shipment.shipper_name)),
            React.createElement("td", { key: emailKey, className: "px-6 py-4 whitespace-nowrap border-b border-gray-200 border-solid text-gray-500", style: { minWidth: '200px', maxWidth: '200px' } },
                React.createElement(CargomaticInput, { id: "bill_of_lading_emails", placeholder: t('COMMA_SEPARATED_EMAILS'), value: shipment.bill_of_lading_emails, onChange: (value) => handleEmailChange(value, shipment) })),
            React.createElement("td", { className: "px-10 py-4 whitespace-nowrap border-b border-grey-lighter border-solid", key: applyAllKey }, !shipment.last_row && (React.createElement(CopyButton, { onClick: () => handleApplyToFollowingShipments(shipment), title: t('APPLY_TO_FOLLOWING_SHIPMENTS') }))),
            React.createElement("td", { className: "px-6 py-4 whitespace-nowrap border-b border-grey-lighter border-solid", key: removeShipmentKey },
                React.createElement("button", { className: "button text-grey-dark hover:text-grey-darker", onClick: () => removeShipment(shipment) },
                    React.createElement("i", { className: "fa fa-remove" }))),
        ];
    };
    const handleEmailChange = (value, shipment) => {
        const index = bolShipments.findIndex((s) => s.id === shipment.id);
        const selectedShipment = bolShipments.find((s) => s.id === shipment.id);
        const allShipments = [
            ...bolShipments.slice(0, index),
            Object.assign(Object.assign({}, selectedShipment), { bill_of_lading_emails: value }),
            ...bolShipments.slice(index + 1),
        ];
        setBolShipments(allShipments);
    };
    const handleApplyToFollowingShipments = (shipment) => {
        const index = bolShipments.findIndex((s) => s.id === shipment.id);
        const followingShipments = [...bolShipments.slice(index + 1)];
        const allShipments = [
            ...bolShipments.slice(0, index + 1),
            ...followingShipments.map((s) => {
                return Object.assign(Object.assign({}, s), { bill_of_lading_emails: shipment.bill_of_lading_emails });
            }),
        ];
        setBolShipments(allShipments);
    };
    const removeShipment = (shipment) => {
        const index = bolShipments.findIndex((s) => s.id === shipment.id);
        const followingShipments = [...bolShipments.slice(index + 1)];
        let allShipments = [...bolShipments.slice(0, index), ...followingShipments];
        allShipments = allShipments.map((s, index, array) => {
            return Object.assign(Object.assign({}, s), { last_row: index === array.length - 1 });
        });
        setBolShipments(allShipments);
    };
    const renderColumnHeaders = () => {
        return (React.createElement("thead", null,
            React.createElement("tr", null, columnHeaders.map((col) => (React.createElement("th", { key: col, className: "leading-none tracking-wide font-medium text-gray-500 px-6 py-3 border-b bg-gray-50 border-gray-200 border-solid text-left text-xs uppercase" }, titleize(t(col.localizationSlug, { count: col.count || 1 }))))))));
    };
    const renderProTip = () => {
        return (React.createElement("div", { className: "mb-4" },
            React.createElement(ProTip, { message: t('FILL_ALL_INFO_BEFORE_APPLY_TO_ALL') })));
    };
    const emailInvalid = (shipment) => {
        const emails = shipment.bill_of_lading_emails.replace(/ /g, '').split(',') || '';
        if (!emails || !emails.length || !Array.isArray(emails)) {
            return true;
        }
        const invalidEmailAddresses = emails.filter((ea) => !validator.validate(ea));
        if (invalidEmailAddresses.length) {
            return true;
        }
        return false;
    };
    const sendAll = async () => {
        var _a, _b, _c, _d;
        const isEmailInvalid = bolShipments.some(emailInvalid);
        if (isEmailInvalid) {
            setError(t('INVALID_EMAILS'));
        }
        else {
            setError('');
            setSaving(true);
            let bolsResponse;
            try {
                bolsResponse = await processBulkBol(bolShipments.map((job) => {
                    return {
                        jobId: job.id,
                        emails: job.bill_of_lading_emails.split(','),
                    };
                }));
            }
            catch (e) {
                showToast(t('FAILED_TO_SEND_BOL'), `${t('FAILED_TO_SEND_BOL')}: ${e}`, 'error');
            }
            if ((_b = (_a = bolsResponse === null || bolsResponse === void 0 ? void 0 : bolsResponse.entity) === null || _a === void 0 ? void 0 : _a.response) === null || _b === void 0 ? void 0 : _b.find((job) => !job.success)) {
                const errors = (_c = bolsResponse === null || bolsResponse === void 0 ? void 0 : bolsResponse.entity) === null || _c === void 0 ? void 0 : _c.response.filter((job) => !job.success).map((job) => job.error);
                showToast(t('FAILED_TO_SEND_BOL'), `${t('FAILED_TO_SEND_BOL')}: ${errors}`, 'error');
            }
            else if ((_d = bolsResponse === null || bolsResponse === void 0 ? void 0 : bolsResponse.entity) === null || _d === void 0 ? void 0 : _d.success) {
                showToast(t('BOL_SENT_SUCCESSFULLY'), t('BOL_SENT_SUCCESSFULLY'), 'success');
            }
            onHide();
        }
    };
    const renderFooter = () => {
        return (React.createElement("div", { className: "flex justify-end" },
            React.createElement("div", { className: "md:w-1/4 mt-3" },
                React.createElement("div", { className: "flex justify-end items-baseline p-4" }, error && (React.createElement("div", null,
                    React.createElement("span", { style: { color: 'red', fontWeight: 500, fontSize: '12.25px' } },
                        `${t('ERROR')}: ${error}`,
                        ' ')))),
                React.createElement("div", { className: "flex justify-end button-group" },
                    React.createElement("button", { className: "block button secondary-action", onClick: onHide }, titleize(t('CLOSE'))),
                    React.createElement("button", { className: "button button-green flex items-center", disabled: !bolShipments.length || saving, onClick: sendAll },
                        saving && (React.createElement("div", { className: "flex items-center mr-2" },
                            React.createElement("i", { className: "fa fa-spinner fa-spin" }))),
                        React.createElement("span", null, t('SEND_ALL')))))));
    };
    const getRefinedShipmentsData = (shipmentFromAPI) => {
        return shipmentFromAPI === null || shipmentFromAPI === void 0 ? void 0 : shipmentFromAPI.map((shipment, index, array) => {
            var _a, _b;
            const notificationPreferences = ((_b = (_a = shipment.shipper) === null || _a === void 0 ? void 0 : _a.notification_preferences) === null || _b === void 0 ? void 0 : _b.filter((pref) => pref.type === 'bill_of_lading_contacts')) || [];
            const bill_of_lading_emails = notificationPreferences.reduce((emails, preferences) => {
                var _a, _b;
                const preferencesEmails = (!((_a = preferences.email) === null || _a === void 0 ? void 0 : _a.silent) &&
                    ((_b = preferences.email.recipients) === null || _b === void 0 ? void 0 : _b.toString())) ||
                    '';
                return (emails +
                    (emails.length && preferencesEmails.length ? ',' : '') +
                    preferencesEmails);
            }, '');
            return {
                id: shipment.id,
                shipment_id: shipment.shipment_id,
                shipper_name: shipment.shipper_name,
                bill_of_lading_emails,
                last_row: index === array.length - 1,
                shipper: shipment.shipper,
            };
        });
    };
    return (React.createElement(CargomaticModal, { show: true, dialogClassName: "modal-override-large bg-grey-modal", onHide: onHide, title: t('SEND_BILL_OF_LADING'), backdrop: "static", body: renderModalComponent(), footer: renderFooter(), location: "middle" }));
};
export default BulkBOLGeneration;
